import { useState } from 'react'
import { NavLinkProps } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { LazyMotion, m } from 'framer-motion'
import { Label } from '@/components/text'
import { colors, breakpoints } from '@/styles'
import { CssUnit } from '@/types'
import { toUnit } from '@/utils/styles'

const loadFeatures = () => import('@/components/animations/features-max').then(res => res.default)

const transformTemplate = (_ = {}, other: string): string => {
  const sep = other.split(', ')
  const joined = sep
    .map((part: string, i: number) => {
      if (i !== 1) return part
      // Make sure transformY is 0px as it moves with scroll
      return '0px'
    })
    .join(', ')
  return joined
}

type Size = 'medium' | 'small'

const primary = css`
  --background: ${colors.grey96};
  --color-normal: ${colors.grey56};
  --color-hover: ${colors.white};
  --color-card: ${colors.blue16};
  --shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  padding: var(--py) var(--px);
`

const small = css`
  --gap: 16px;
  --px: 8px;
  --py: 8px;
  --card: 28px;
  --card-x: 16px;
`

const medium = css`
  --gap: 4px;
  --px: 4px;
  --py: 4px;
  --card: 32px;
  --card-x: 12px;
`

const getLabelStyles = (size: Size): { fontSize: '12px' | '14px' } => {
  if (size === 'small') return { fontSize: '12px' }
  return { fontSize: '14px' }
}

const getVariantStyles = () => {
  return primary
}

const getSizeStyles = (props: TabMenuStyle) => {
  if (props.size === 'small') return small
  return medium
}

const Wrapper = styled.nav<Omit<TabMenuStyle, 'options'>>`
  ${props => getSizeStyles(props)};
  ${getVariantStyles()};

  display: flex;
  gap: 0 var(--gap);
  background: var(--background);
  border-radius: 6px;
  width: 100%;
  padding: 4px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-bottom: 20px;
  }
`

const Selector = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: var(--color-card);
  color: ${colors.white};
  border-radius: 8px;
  pointer-events: none;
  box-shadow: var(--shadow);
`

const TabWrapper = styled.div.withConfig<{ height?: CssUnit }>({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    defaultValidatorFn(prop) && ['height'].indexOf(prop) < 0
})`
  position: relative;
  height: ${props => (props.height ? toUnit(props.height) : 'var(--card)')};
  display: inline-flex;
  align-items: center;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex: 1 1 0px;
    justify-content: center;
  }
`

const BoldText = styled.span`
  display: block;
  content: attr(data-text);
  font-weight: 600;
  height: 1px;
  color: transparent;
  overflow: hidden;
  visibility: hidden;
`

const TabLink = styled.button`
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  user-select: none;
  z-index: 10;
  padding: 0 var(--card-x);
  height: 100%;
  display: inline-flex;
  align-items: center;
  color: var(--color-normal);
  transition: 0.15s ease;
  transition-property: color;
  border: none;
  background: none;
  cursor: pointer;

  &[aria-selected='true'] {
    color: var(--color-hover);
    > span {
      font-weight: 600;
    }
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 0;
  }
`

type TabProps = Omit<NavLinkProps, 'to'> & {
  key: string
  to?: string
  onClick?: () => void
  active?: boolean
}

type TabMenuStyle = {
  size: Size
  height?: CssUnit
}

type TabMenuProps = TabMenuStyle & {
  options: TabProps[]
}

const TabMenu: React.FC<TabMenuProps> = ({ options, size, height, ...rest }) => {
  const current = (() => {
    const matches = options.map(({ key, isActive }) => (isActive ? key : '')).filter(v => !!v)
    return matches?.[0] ?? ''
  })()
  const [active, setActive] = useState('')
  const selected = active || current
  return (
    <LazyMotion features={loadFeatures}>
      <Wrapper size={size} {...rest}>
        {options.map(({ key, children, ...props }) => (
          <TabWrapper key={key} height={height}>
            {selected === key && (
              <Selector
                as={m.div}
                layoutId="example-tab-select"
                transformTemplate={transformTemplate}
              />
            )}
            {/* @ts-ignore */}
            <TabLink
              {...props}
              onMouseEnter={() => setActive(key)}
              onMouseLeave={() => setActive('')}
              onFocus={() => setActive(key)}
              onBlur={() => setActive('')}
              aria-selected={selected === key}
            >
              <Label as="span" {...getLabelStyles(size)}>
                {children}
                <BoldText aria-hidden>{children}</BoldText>
              </Label>
            </TabLink>
          </TabWrapper>
        ))}
      </Wrapper>
    </LazyMotion>
  )
}

export default TabMenu
